import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../services/authService';
import { setSentryUser, clearSentryUser } from '../config/sentry';

interface SubscriptionInfo {
  id: string;
  status: string;
  planName: string;
  currentPeriodEnd: string | null;
  trialEnd: string | null;
}

interface User {
  id: string;
  email: string;
  emailVerified: boolean;
  profile: {
    id: string;
    displayName: string | null;
    fullName?: string | null;
    email: string;
    avatarUrl: string | null;
    persona: string;
    bio?: string | null;
    location?: string | null;
    organizationId?: string | null;
    organizationRole?: string | null;
    onboardingCompleted?: boolean;
    isNewUser?: boolean;
  };
  subscription?: SubscriptionInfo | null;
  hasActiveSubscription?: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name?: string) => Promise<{ message: string; email: string; emailVerified: boolean }>;
  signInWithGoogle: (idToken: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshToken = async () => {
    const refreshTokenValue = localStorage.getItem('refresh_token');
    if (!refreshTokenValue) {
      throw new Error('No refresh token');
    }

    const response = await authService.refreshToken(refreshTokenValue);
    
    localStorage.setItem('access_token', response.accessToken);
    
    // Get updated user info
    const currentUser = await authService.getCurrentUser();
    setUser(currentUser);
    // Set Sentry user context
    if (currentUser?.profile?.id) {
      setSentryUser({
        id: currentUser.profile.id,
        email: currentUser.email,
        username: currentUser.profile.displayName || undefined,
      });
    }
  };

  useEffect(() => {
    // Check if user is already authenticated on mount
    const initAuth = async () => {
      const accessToken = localStorage.getItem('access_token');
      const refreshTokenValue = localStorage.getItem('refresh_token');
      
      // Only try to authenticate if we have tokens
      if (accessToken && refreshTokenValue) {
        try {
          const currentUser = await authService.getCurrentUser();
          setUser(currentUser);
          // Set Sentry user context
          if (currentUser?.profile?.id) {
            setSentryUser({
              id: currentUser.profile.id,
              email: currentUser.email,
              username: currentUser.profile.displayName || undefined,
            });
          }
        } catch (error) {
          // Token might be expired, try to refresh
          try {
            await refreshToken();
          } catch (refreshError) {
            // Refresh failed, clear tokens and user
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            setUser(null);
            clearSentryUser();
          }
        }
      } else {
        // No tokens, ensure user is null
        setUser(null);
        clearSentryUser();
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string) => {
    const response = await authService.login(email, password);
    
    localStorage.setItem('access_token', response.accessToken);
    localStorage.setItem('refresh_token', response.refreshToken);
    
    setUser(response.user);
    // Set Sentry user context
    if (response.user?.profile?.id) {
      setSentryUser({
        id: response.user.profile.id,
        email: response.user.email,
        username: response.user.profile.displayName || undefined,
      });
    }
  };

  const register = async (email: string, password: string, name?: string) => {
    const response = await authService.register(email, password, name);
    
    // Don't set tokens - user needs to verify email first
    // Return response for UI to show success message
    return response;
  };

  const signInWithGoogle = async (idToken: string) => {
    const response = await authService.googleOAuth(idToken);
    
    localStorage.setItem('access_token', response.accessToken);
    localStorage.setItem('refresh_token', response.refreshToken);
    
    setUser(response.user);
    // Set Sentry user context
    if (response.user?.profile?.id) {
      setSentryUser({
        id: response.user.profile.id,
        email: response.user.email,
        username: response.user.profile.displayName || undefined,
      });
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      setUser(null);
      clearSentryUser();
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    signInWithGoogle,
    logout,
    refreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
